import { getLocale } from '@tov-plus/ui';
import { compareEventDisplayOrder } from '@tov-plus/domain';
import { msg, TovButton, TovCard, TovIcon, useToast } from '@tov-plus/ui';
import { NavLink, useParams } from '@tov-plus/frontend-data';

import { StatePanel } from '../components/StatePanel';
import { useGuestAccess } from '../contexts/GuestAccessContext';
import { useGuestLiveUpdates } from '../lib/communications/useGuestLiveUpdates';
import { formatGuestDateTime } from '../lib/localization/guestLocale';

function formatCalendarDate(value: string) {
  return new Date(value).toISOString().replaceAll('-', '').replaceAll(':', '');
}

function escapeCalendarText(value: string) {
  return value
    .replaceAll('\\', '\\\\')
    .replaceAll(';', '\\;')
    .replaceAll(',', '\\,')
    .replaceAll('\n', '\\n');
}

function downloadEventCalendar(input: {
  filename: string;
  summary: string;
  startsAt: string;
  endsAt: string | null;
  location: string | null;
  description: string | null;
  uid: string;
}) {
  const contents = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Tov Plus//Guest Event//EN',
    'CALSCALE:GREGORIAN',
    'BEGIN:VEVENT',
    `UID:${escapeCalendarText(input.uid)}`,
    `DTSTAMP:${formatCalendarDate(new Date().toISOString())}`,
    `DTSTART:${formatCalendarDate(input.startsAt)}`,
    input.endsAt ? `DTEND:${formatCalendarDate(input.endsAt)}` : null,
    `SUMMARY:${escapeCalendarText(input.summary)}`,
    input.location ? `LOCATION:${escapeCalendarText(input.location)}` : null,
    input.description
      ? `DESCRIPTION:${escapeCalendarText(input.description)}`
      : null,
    'END:VEVENT',
    'END:VCALENDAR',
  ]
    .filter((line): line is string => line !== null)
    .join('\r\n');
  const blob = new Blob([`${contents}\r\n`], {
    type: 'text/calendar;charset=utf-8',
  });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = input.filename;
  link.click();
  window.URL.revokeObjectURL(url);
}

function buildGoogleCalendarUrl(input: {
  title: string;
  startsAt: string;
  endsAt: string | null;
  location: string | null;
  details: string | null;
}) {
  const dates = `${formatCalendarDate(input.startsAt)}/${formatCalendarDate(
    input.endsAt ?? input.startsAt,
  )}`;
  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: input.title,
    dates,
  });

  if (input.location) {
    params.set('location', input.location);
  }
  if (input.details) {
    params.set('details', input.details);
  }

  return `https://calendar.google.com/calendar/render?${params.toString()}`;
}

function buildOutlookCalendarUrl(input: {
  title: string;
  startsAt: string;
  endsAt: string | null;
  location: string | null;
  details: string | null;
}) {
  const params = new URLSearchParams({
    path: '/calendar/action/compose',
    rru: 'addevent',
    subject: input.title,
    startdt: new Date(input.startsAt).toISOString(),
    enddt: new Date(input.endsAt ?? input.startsAt).toISOString(),
  });

  if (input.location) {
    params.set('location', input.location);
  }
  if (input.details) {
    params.set('body', input.details);
  }

  return `https://outlook.live.com/calendar/0/deeplink/compose?${params.toString()}`;
}

export function EventDetailPage() {
  const access = useGuestAccess();
  const liveUpdates = useGuestLiveUpdates(access);
  const notifications = useToast();
  const { eventId } = useParams();

  if (access.status !== 'ready') {
    return null;
  }

  const events = [...access.experience.events].sort(compareEventDisplayOrder);
  const event = events.find((item) => item.id === eventId) ?? null;

  if (!event) {
    return (
      <StatePanel
        preset="empty"
        actions={
          <NavLink className="secondary-link" to="/events">
            {msg('general.modules.events.name')}{' '}
          </NavLink>
        }
      />
    );
  }

  const localeCode = getLocale();
  const timezone = event.timezone ?? access.experience.occasion.timezone;
  const location = [event.locationName, event.locationAddress]
    .filter(Boolean)
    .join(', ');
  const mapHref = event.locationAddress
    ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
        event.locationAddress,
      )}`
    : null;
  const updateCount = access.experience.recentCommunications.filter(
    (item) => item.event?.id === event.id,
  ).length;
  const hasUnreadUpdate = liveUpdates.hasUnreadEventUpdate(event.id);
  const eventTitle = event.displayName;
  const occasionName = access.experience.occasion.displayName;
  const eventStartLabel = event.startsAt
    ? formatGuestDateTime(event.startsAt, localeCode, timezone)
    : msg('pages.guest_home_page_sections.time_to_be_announced');
  const eventLocationLabel = location || msg('general.states.address_pending');
  const googleCalendarUrl = event.startsAt
    ? buildGoogleCalendarUrl({
        title: event.displayName,
        startsAt: event.startsAt,
        endsAt: event.endsAt,
        location: location || null,
        details: event.guestSummary ?? event.description,
      })
    : null;
  const outlookCalendarUrl = event.startsAt
    ? buildOutlookCalendarUrl({
        title: event.displayName,
        startsAt: event.startsAt,
        endsAt: event.endsAt,
        location: location || null,
        details: event.guestSummary ?? event.description,
      })
    : null;

  async function shareEvent() {
    const url = window.location.href;
    const eventShareText = [
      `${occasionName}: ${eventTitle}`,
      eventStartLabel,
      eventLocationLabel,
      msg('pages.event_detail_page.share_event_access_guidance'),
    ].join('\n');

    if (navigator.share) {
      await navigator.share({
        title: eventTitle,
        text: eventShareText,
        url,
      });
      return;
    }

    await navigator.clipboard.writeText(`${eventShareText}\n${url}`);
    notifications.success(
      msg('pages.guest_home_page_sections.invite_link_copied'),
    );
  }

  return (
    <section className="page-grid">
      <TovCard variant="content" as="div" isWide className="hero-card">
        <p className="page-eyebrow">
          {msg('pages.event_detail_page.event_details')}
        </p>
        <h2>{event.displayName}</h2>
        <p>
          {event.guestSummary ??
            event.description ??
            msg('pages.event_detail_page.hosts_will_share_more_details_here')}
        </p>
        <div className="hero-card__stats">
          <article>
            <strong>
              {event.startsAt
                ? formatGuestDateTime(event.startsAt, localeCode, timezone)
                : msg('pages.guest_home_page_sections.time_to_be_announced')}
            </strong>
            <span>{msg('pages.events_page.pages_events_page_when')}</span>
          </article>
          <article>
            <strong>
              {event.locationName ?? msg('general.states.pending')}
            </strong>
            <span>{msg('pages.events_page.pages_events_page_where')}</span>
          </article>
          <article>
            <strong>{updateCount}</strong>
            <span>{msg('pages.event_detail_page.event_specific_updates')}</span>
          </article>
        </div>
        <div className="action-row">
          {event.startsAt ? (
            <TovButton
              type="button"
              onClick={() => {
                downloadEventCalendar({
                  filename: `${event.eventKey || event.id}.ics`,
                  summary: event.displayName,
                  startsAt: event.startsAt ?? event.createdAt,
                  endsAt: event.endsAt,
                  location: location || null,
                  description: event.guestSummary ?? event.description,
                  uid: `${access.experience.occasion.id}-${event.id}@tov.plus`,
                });
                notifications.success(
                  msg('pages.guest_home_page_sections.calendar_file_ready'),
                );
              }}
            >
              <TovIcon name="calendar-blank" />
              {msg('pages.guest_home_page_sections.add_to_calendar')}{' '}
            </TovButton>
          ) : null}
          {googleCalendarUrl ? (
            <a
              className="secondary-link"
              href={googleCalendarUrl}
              rel="noreferrer"
              target="_blank"
            >
              {msg('pages.event_detail_page.open_google_calendar')}{' '}
            </a>
          ) : null}
          {outlookCalendarUrl ? (
            <a
              className="secondary-link"
              href={outlookCalendarUrl}
              rel="noreferrer"
              target="_blank"
            >
              {msg('pages.event_detail_page.open_outlook_calendar')}{' '}
            </a>
          ) : null}
          {mapHref ? (
            <a className="secondary-link" href={mapHref}>
              {msg('pages.guest_home_page_sections.open_maps')}{' '}
            </a>
          ) : null}
          <TovButton
            type="button"
            variant="secondary"
            onClick={() => void shareEvent()}
          >
            <TovIcon name="copy" />
            {msg('pages.guest_home_page_sections.share_with_household')}{' '}
          </TovButton>
        </div>
      </TovCard>

      <TovCard variant="content" isWide>
        <p className="page-eyebrow">
          {msg('pages.event_detail_page.guest_checklist')}
        </p>
        <div className="detail-list">
          <article>
            <strong>{msg('general.modules.rsvp.name')}</strong>
            <span>
              {msg(
                'pages.event_detail_page.review_your_response_for_this_event',
              )}
            </span>
            <NavLink className="secondary-link" to="/rsvp">
              {msg('app.guest_workflows.review_rsvp')}{' '}
            </NavLink>
          </article>
          <article>
            <strong>
              {msg('pages.today_page.pages_today_page_travel_guidance')}
            </strong>
            <span>{location || msg('general.states.address_pending')}</span>
            <NavLink className="secondary-link" to="/locations">
              {msg('general.modules.locations.name')}{' '}
            </NavLink>
          </article>
          <article>
            <strong>{msg('general.modules.messages.name')}</strong>
            <span>
              {hasUnreadUpdate
                ? msg('pages.event_detail_page.unread_update_needs_review')
                : msg('pages.event_detail_page.no_unread_event_update')}
            </span>
            <NavLink className="secondary-link" to="/messages">
              {msg('pages.events_page.pages_events_page_review_messages')}{' '}
            </NavLink>
          </article>
        </div>
      </TovCard>
    </section>
  );
}
